/**
 * Console trace for the passkey login path.
 *
 * The failure mode this exists for — "the browser showed the cross-device QR instead of my local
 * passkey prompt" — is invisible from the server side. The only evidence is on the client: the
 * `allowCredentials` list the ceremony sent, the `rpId` it resolved, what the authenticator
 * answered, and whether the account's device records agree with what this browser holds.
 *
 * Everything logged here is public material (credential ids, PRF salts, device names). No PRF
 * output, no master seed, no session token is ever passed to this module.
 *
 * On by default while the login flow is being diagnosed; silence it with
 * `localStorage["tensile:auth-debug"] = "0"`.
 */

import {
  base64ToBytes,
  resolveEffectiveRpId,
  type CeremonyDebug,
  type WrappedDeviceMasterKey,
} from "@tensilelabs/auth-client";

const PREFIX = "[taskjinn-auth]";
const FLAG_KEY = "tensile:auth-debug";

export function authDebugEnabled(): boolean {
  try {
    return localStorage.getItem(FLAG_KEY) !== "0";
  } catch {
    // Storage blocked: keep the trace, it is the only diagnostic a locked-down browser leaves.
    return true;
  }
}

export function authDebug(event: string, data?: Record<string, unknown>): void {
  if (!authDebugEnabled()) return;
  if (data) console.info(PREFIX, event, data);
  else console.info(PREFIX, event);
}

/** The sink the SDK ceremonies report through. */
export const ceremonyDebug: CeremonyDebug = (event, data) => authDebug(event, data);

function byteLengthOrReason(b64: string): number | string {
  try {
    return base64ToBytes(b64).byteLength;
  } catch {
    return `invalid base64: ${b64.slice(0, 16)}…`;
  }
}

/**
 * A device envelope as it appears on the wire.
 *
 * `credentialIdB64` is the value that goes into `allowCredentials`; a browser can only satisfy the
 * ceremony when one of these is the credential it actually holds. `credentialIdBytes` catches a
 * malformed record (base64url, truncated, or an id that was never a credential).
 */
export function describeEnvelope(envelope: WrappedDeviceMasterKey): Record<string, unknown> {
  return {
    deviceId: envelope.device_id,
    deviceName: envelope.device_name,
    credentialIdB64: envelope.credential_id_b64,
    credentialIdBytes: byteLengthOrReason(envelope.credential_id_b64),
    prfSaltB64: envelope.prf_salt_b64,
    wrappedSeedBytes: byteLengthOrReason(envelope.wrapped_seed_b64),
  };
}

/** What this browser can do and which rpId the ceremonies will use. */
export async function describeClientCapabilities(): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = {
    hostname: window.location.hostname,
    origin: window.location.origin,
    secureContext: window.isSecureContext,
    resolvedRpId: resolveEffectiveRpId("tensilelabs.com"),
    hasPublicKeyCredential: typeof window.PublicKeyCredential !== "undefined",
    hasCredentialsGet: Boolean(navigator.credentials?.get),
    hasCredentialsCreate: Boolean(navigator.credentials?.create),
  };

  const api = window.PublicKeyCredential as unknown as
    | {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
        isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
      }
    | undefined;

  try {
    if (api?.getClientCapabilities) {
      out.clientCapabilities = await api.getClientCapabilities();
    }
    if (api?.isUserVerifyingPlatformAuthenticatorAvailable) {
      out.platformAuthenticatorAvailable =
        await api.isUserVerifyingPlatformAuthenticatorAvailable();
    }
  } catch (cause) {
    out.capabilityError = cause instanceof Error ? cause.message : String(cause);
  }

  return out;
}
